/** Bounded, copy/URL-free evidence; safe for general logs and status polling. */
export type TextEditDiagnostic = {
  kind: "text_copy_delivery";
  stage: "source" | "provider" | "old_cleanup" | "replacement" | "final";
  reason: "neighbor_collision" | "safe_boundary" | "copy_unreadable" | "source_ambiguous" | "old_copy_remaining" | "fit_unavailable" | "placement_unchanged" | "style_unchanged" | "delivery_changed";
  sourceBox?: { x: number; y: number; width: number; height: number };
  replacementBox?: { x: number; y: number; width: number; height: number };
  obstacle?: { x: number; y: number; width: number; height: number };
  protectionSource?: "full_frame" | "neighbor_strip";
};

/** Never serialize arbitrary error extensions, OCR strings or upstream data. */
export function safeTextEditDiagnostic(value: unknown): TextEditDiagnostic | null {
  if (!value || typeof value !== "object") return null;
  const d = value as Record<string, unknown>;
  const stages = ["source", "provider", "old_cleanup", "replacement", "final"];
  const reasons = ["neighbor_collision", "safe_boundary", "copy_unreadable", "source_ambiguous",
    "old_copy_remaining", "fit_unavailable", "placement_unchanged", "style_unchanged", "delivery_changed"];
  if (d.kind !== "text_copy_delivery" || !stages.includes(d.stage as string) || !reasons.includes(d.reason as string)) return null;
  const result: TextEditDiagnostic = { kind: "text_copy_delivery",
    stage: d.stage as TextEditDiagnostic["stage"], reason: d.reason as TextEditDiagnostic["reason"] };
  for (const key of ["sourceBox", "replacementBox", "obstacle"] as const) {
    const box = d[key] as Record<string, unknown> | undefined;
    if (box && ["x", "y", "width", "height"].every(k => typeof box[k] === "number" &&
        Number.isFinite(box[k]) && (box[k] as number) >= 0 && (box[k] as number) <= 100_000) &&
        (box.width as number) > 0 && (box.height as number) > 0)
      result[key] = { x: box.x as number, y: box.y as number, width: box.width as number, height: box.height as number };
  }
  if (d.protectionSource === "full_frame" || d.protectionSource === "neighbor_strip") result.protectionSource = d.protectionSource;
  return result;
}
